import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { useTRPCAPI, useTRPCMutation } from '../../../../trpc/trpc';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';
import { useLicenseInfo } from '../../../../redux/slices/license/hooks/hooks';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { getUpgradeSessionErrorMessage } from '../../utils/getUpgradeSessionErrorMessage';
import { hasTrackingConsent } from '../../../analytics/consent/trackingConsent';
import { Music24Plan, Music24PlanId, music24CheckoutPlanInput } from '../../config/music24StripePlans';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import {
  Music24PlanCapabilities,
  SMLUpgradeFeature,
  getPlanCapabilities,
  pickPlanForFeature,
  plansForFeature,
} from '../../config/music24PlanCapabilities';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { ExtraArtistOffer, extraArtistOfferFor } from './extraArtistOffer';
import { PlanStore, usePlanStore } from './usePlanStore';
import { rememberUpsellCheckoutSurface } from '../../watermarkUpsell/upsellCheckoutSurface';
import { useIsUpsellMember } from '../../watermarkUpsell/useIsUpsellMember';
import { useUpsellEventFacts } from '../../watermarkUpsell/useUpsellEventFacts';
import { trackUpsellCheckoutStarted } from '../../watermarkUpsell/watermarkUpsellAnalytics';
import { SMLAnalytics } from '../../../analytics/sml/analytics';
import { useMusic24Plan } from '../../hooks/useMusic24Plan';

export interface UpgradeOffer {
  /** The selected plan. Until the person picks one, the cheapest plan that unlocks the feature. */
  plan: Music24Plan;
  planCapabilities: Music24PlanCapabilities;
  currentCapabilities: Music24PlanCapabilities;
  hasPaidPlan: boolean;
  /**
   * Design b's plan list: every plan that unlocks the feature, cheapest first. It holds at least
   * `plan`, also when no plan gives more (the fallback offer).
   */
  plans: Music24Plan[];
  /** `null` goes back to the cheapest plan. */
  selectPlan: (planId: Music24PlanId | null) => void;
  /** The monthly price of `plan`, formatted in the display currency. */
  monthlyPrice: string;
  email: string | undefined;
  subscriptionExpiresAt: number | null;
  isCheckoutPending: boolean;
  startCheckout: () => void;
  /**
   * Only for `artists` on Plus, Pro, Max or Ultra below the cap: one more paid artist on the current
   * plan at the plan price minus 20 % ("Add an artist for $31.20 a month" on Pro). `startCheckout`
   * then buys the artist, not another plan.
   */
  extraArtist: ExtraArtistOffer | null;
  /** The monthly price of one extra artist, formatted in the display currency. Null with no offer. */
  extraArtistPrice: string | null;
  /**
   * The store that bills the current plan (App Store, Google Play), else null. A store plan changes
   * in the store: the prompt shows the store note and starts no web checkout.
   */
  planStore: PlanStore | null;
}

export interface UpgradeOfferOptions {
  /**
   * The range the person actually asked for. Without it the offer is the cheapest plan that adds
   * any history at all, which can be a plan that still falls short of the chip they clicked.
   */
  requiredHistoryDays?: number;
  /**
   * The plan that is selected until the person picks one, when the offer holds it. Absent or null:
   * the cheapest plan. The watermark offer selects Pro when a file needs it (design trial-upsell b4).
   */
  defaultPlanId?: Music24PlanId | null;
  /**
   * The upsell surface that opened the offer (trial upsell spec 2026-10-13 §5.4). It goes into the
   * Stripe Checkout session metadata and into `upsell_checkout_started` and `purchase_completed`.
   */
  upsellSurface?: UpsellSurface;
}

/**
 * Everything the upgrade modal needs to make a priced, contextual offer: the plans that unlock
 * what the person clicked (the cheapest one selected first), what they cost, and how to buy one.
 */
export function useUpgradeOffer(feature: SMLUpgradeFeature, options?: UpgradeOfferOptions): UpgradeOffer {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const trpcAPI = useTRPCAPI();
  const licenseInfo = useLicenseInfo();
  const userTokenDecoded = useUserTokenDecoded();
  const [selectedPlanId, setSelectedPlanId] = useState<Music24PlanId | null>(null);
  const [isCheckoutPending, setCheckoutPending] = useState(false);

  const hasPaidPlan = licenseInfo.isSubscriptionValid();
  // Whether to buy through the endpoint is decided by login state, NOT `hasPaidPlan`.
  // `isSubscriptionValid()` goes false the moment `subscriptionExpiresAt` passes — exactly the
  // case a `past_due`/`unpaid` Stripe subscriber is in after a card decline. Routing that customer
  // on `hasPaidPlan` would send them to the public Checkout endpoint, which has no subscription
  // detection at all, and open a second live subscription. `createMusic24UpgradeSession` does that
  // detection server-side (including past_due/unpaid) and falls back to Checkout itself when there
  // really is no subscription, so every logged-in caller must go through it — mirrors
  // `SMLPricing.tsx`, which already gates on login state for the same reason.
  const isLoggedIn = userTokenDecoded !== null;
  // Read once here rather than through `userTokenDecoded?.email` inside the branch below: with
  // `isLoggedIn` computed from `userTokenDecoded !== null`, TS's aliased-condition narrowing
  // otherwise narrows `userTokenDecoded` to `never` in the `else` branch of `isLoggedIn ? ... :
  // ...` for this particular (Redux/Immer-typed) value.
  const userEmail = userTokenDecoded?.email;
  const { showError } = useShowToast();
  // App Store 3.1.1 / Play billing: a native app's web view never starts a Stripe checkout. The
  // prompt shows no checkout button there; this is the last line of defense.
  const isNativeEmbed = useIsNativeEmbed();
  // Only the workspace owner pays (ruling 8). The prompt shows a member the owner note and no
  // button; this is the last line of defense for a member.
  const isMember = useIsUpsellMember();

  const { mutateAsync: createCheckoutSession } = useTRPCMutation(
    trpcAPI.billing.createMusic24CheckoutSession.mutationOptions()
  );
  const { mutateAsync: createUpgradeSession } = useTRPCMutation(
    trpcAPI.billing.createMusic24UpgradeSession.mutationOptions()
  );

  // The licence slice, not the session token: the token's claims are frozen for up to an hour, so
  // after a plan change they would offer the wrong plans. `LockedControl` reads the same source, so
  // its chip and this prompt always name the same plan.
  const currentCapabilities: Music24PlanCapabilities = useMusic24Capabilities();

  const requiredHistoryDays = options?.requiredHistoryDays;
  // Raise the bar to the range they asked for, so every offered plan clears it.
  const barCapabilities = useMemo<Music24PlanCapabilities>(
    () =>
      requiredHistoryDays === undefined
        ? currentCapabilities
        : {
            ...currentCapabilities,
            historyDaysLimit: Math.max(currentCapabilities.historyDaysLimit, requiredHistoryDays - 1),
          },
    [currentCapabilities, requiredHistoryDays]
  );

  const cheapestPlan = useMemo(() => pickPlanForFeature(feature, barCapabilities), [feature, barCapabilities]);

  // Cheapest first (`plansForFeature`).
  const plans = useMemo(() => {
    const unlocking = plansForFeature(feature, barCapabilities);
    return unlocking.length > 0 ? unlocking : [cheapestPlan];
  }, [feature, barCapabilities, cheapestPlan]);

  // The paywall funnels (`webCheckout`, `limitToUpgrade`) start with this event.
  const currentPlan = useMusic24Plan();
  useEffect(() => {
    SMLAnalytics.track('paywall_viewed', { reason: feature, kind: 'plans', plan: currentPlan }).catch(console.error);
    // One event for each prompt that comes on screen, not for each plan change of the visitor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);

  const defaultPlanId = options?.defaultPlanId ?? null;
  const upsellSurface = options?.upsellSurface;
  const upsellEventFacts = useUpsellEventFacts();
  const plan = useMemo(
    () =>
      plans.find((candidate) => candidate.id === selectedPlanId) ??
      plans.find((candidate) => candidate.id === defaultPlanId) ??
      cheapestPlan,
    [plans, selectedPlanId, defaultPlanId, cheapestPlan]
  );
  const planCapabilities = useMemo(() => getPlanCapabilities(plan), [plan]);
  const planStore = usePlanStore();
  const extraArtist = useMemo(
    () => extraArtistOfferFor(feature, currentCapabilities, planStore),
    [feature, currentCapabilities, planStore]
  );

  // Design b sells monthly only; the plans window keeps the yearly choice.
  const startCheckout = useCallback(() => {
    // The server refuses a web checkout next to a store plan (`M24_STORE_SUBSCRIPTION`).
    if (isNativeEmbed || isMember || planStore !== null) {
      return;
    }
    setCheckoutPending(true);
    // The server finds the current Stripe price of the tier: the web holds no price id. An extra
    // artist keeps the current plan and asks for one more paid artist.
    const planInput =
      extraArtist === null
        ? music24CheckoutPlanInput(plan, 'monthly')
        : { tier: extraArtist.tier, interval: 'monthly' as const, extraArtists: extraArtist.extraArtists };

    // A logged-in caller always goes through the upgrade endpoint: it resolves their subscription
    // server-side (including past_due/unpaid, not just "currently valid") and either lands them in
    // Stripe already authenticated on the plan-change confirm screen, or falls back to Checkout
    // itself when there really is no subscription. Only a signed-out (marketing) visitor uses the
    // public Checkout endpoint directly.
    SMLAnalytics.track('paywall_cta_tapped', { reason: feature, tier: planInput.tier, interval: 'month' }).catch(
      console.error
    );
    if (upsellSurface !== undefined) {
      // `PaymentCompleteView` reads it back after the Stripe trip (`purchase_completed`).
      rememberUpsellCheckoutSurface(upsellSurface);
      trackUpsellCheckoutStarted({ ...upsellEventFacts(upsellSurface), tier: planInput.tier, interval: 'monthly' });
    } else {
      SMLAnalytics.track('checkout_started', {
        kind: 'plan',
        tier: planInput.tier,
        interval: 'month',
        surface: 'none',
      }).catch(console.error);
    }
    const request = isLoggedIn
      ? createUpgradeSession({
          ...planInput,
          returnUrl: window.location.href,
          trackingConsent: hasTrackingConsent(),
          // Checkout charges the currency of the price on screen (decision D7).
          currency,
          ...(upsellSurface === undefined ? {} : { upsellSurface }),
        })
      : createCheckoutSession({
          ...planInput,
          successUrl: `${window.location.origin}/app/paymentComplete?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
          customerEmail: userEmail,
        });

    request
      .then((result) => {
        if (result.url) {
          window.location.href = result.url;
        }
        setCheckoutPending(false);
        return undefined;
      })
      .catch((error: unknown) => {
        // The raw error can carry internal Stripe text — log it, but never show it to the
        // customer. Only a small, translated set of messages reaches the toast.
        console.error(error);
        setCheckoutPending(false);
        showError(getUpgradeSessionErrorMessage(error, intl));
      });
  }, [
    isNativeEmbed,
    isMember,
    planStore,
    isLoggedIn,
    createUpgradeSession,
    createCheckoutSession,
    plan,
    extraArtist,
    userEmail,
    showError,
    intl,
    upsellSurface,
    upsellEventFacts,
    feature,
    currency,
  ]);

  return {
    plan,
    planCapabilities,
    currentCapabilities,
    hasPaidPlan,
    plans,
    selectPlan: setSelectedPlanId,
    monthlyPrice: formatPlanPrice(intl, plan.prices.monthly.amount, currency),
    email: userEmail,
    subscriptionExpiresAt: userTokenDecoded?.licenseInfo?.subscriptionExpiresAt ?? null,
    isCheckoutPending,
    startCheckout,
    extraArtist,
    extraArtistPrice: extraArtist === null ? null : formatPlanPrice(intl, extraArtist.monthlyCents, currency),
    planStore,
  };
}
